// utils/generateUserID.js
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../configs/firebase';

/**
 * Generates a unique intern ID in format: IN + 5 digits + first letter of first name
 * Example: IN12345J (for John), IN67890M (for Mary)
 */
export const generateInternID = async (firstName) => {
  const maxAttempts = 10;
  let attempts = 0;
  
  if (!firstName || firstName.trim().length === 0) {
    throw new Error('First name is required to generate intern ID');
  }
  
  const firstLetter = firstName.trim().charAt(0).toUpperCase();
  
  while (attempts < maxAttempts) {
    // Generate 5 random digits
    const fiveDigits = Math.floor(10000 + Math.random() * 90000); // Generates 10000-99999
    const internID = `IN${fiveDigits}${firstLetter}`;
    
    try {
      // Check if this ID already exists in the database
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('internID', '==', internID));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        // ID is unique, return it
        return internID;
      }
      
      attempts++;
    } catch (error) {
      console.error('Error checking intern ID uniqueness:', error);
      attempts++;
    }
  }
  
  throw new Error('Unable to generate unique intern ID after maximum attempts');
};

/**
 * Validates intern ID format
 */
export const validateInternID = (internID) => {
  const pattern = /^IN\d{5}[A-Z]$/;
  return pattern.test(internID);
};

/**
 * Extracts information from intern ID
 */
export const parseInternID = (internID) => {
  if (!validateInternID(internID)) {
    throw new Error('Invalid intern ID format');
  }
  
  return {
    prefix: internID.substring(0, 2), // "IN"
    number: internID.substring(2, 7), // 5 digits
    firstLetter: internID.substring(7, 8), // First letter of name
    isValid: true
  };
};

// Example usage:
/*
const internID = await generateInternID("John");
console.log(internID); // Output: IN12345J

const validation = validateInternID("IN12345J");
console.log(validation); // Output: true

const parsed = parseInternID("IN12345J");
console.log(parsed); 
// Output: { prefix: "IN", number: "12345", firstLetter: "J", isValid: true }
*/